import { fetchWithAuth } from '../utils/api';
import { notify, isModuleOn } from './store';

/**
 * Roles, the checklist they are built from, and what the signed-in user may do.
 *
 * All three come from the server. The checklist in particular is not written
 * out again here: the browser and the middleware have to agree about what
 * "inventory.stock: delete" means, and the only way to guarantee that is for
 * both to read the same list. A second copy in JavaScript would agree on the
 * day it was written and drift from the next feature onwards.
 */

// Every role this organization has, permissions included.
export const roles = [];

// The checklist: one line per feature the organization actually has, already
// filtered by its type and the modules it has bought.
export const accessFeatures = [];

// What the signed-in user may do. `root` is the administrator — everything,
// including features added after their role was saved.
let mine = { roleId: '', roleName: '', root: false, features: {} };

export const myAccess = () => mine;

/**
 * can('inventory.stock', 'delete') — the one question the screens ask.
 *
 * It answers true while the user's access has not loaded yet. A screen that
 * hides its buttons for the half-second before the answer arrives flickers
 * every menu on every page load, and the server refuses anything the user
 * should not have anyway — the screen is a convenience, not the lock.
 */
export const can = (feature, action = 'view') => {
  if (!loaded) return true;
  if (mine.root) return true;
  const got = mine.features[feature];
  return !!(got && got[action]);
};

/** canAny — for a menu heading that should appear if any child is allowed. */
export const canAny = (features, action = 'view') =>
  features.some((f) => can(f, action));

let loaded = false;
export const accessLoaded = () => loaded;

export const loadMyAccess = async () => {
  try {
    const res = await fetchWithAuth('/access/me');
    if (!res.ok) return;
    const data = await res.json();
    mine = {
      roleId: data.roleId || '',
      roleName: data.roleName || '',
      root: !!data.root,
      features: data.features || {},
    };
    loaded = true;
    notify();
  } catch (e) {
    console.error('Failed to load access', e);
  }
};

export const loadAccessFeatures = async () => {
  try {
    const res = await fetchWithAuth('/access/features');
    if (!res.ok) return;
    const data = await res.json();
    accessFeatures.length = 0;
    accessFeatures.push(...(Array.isArray(data) ? data : []));
    notify();
  } catch (e) {
    console.error('Failed to load the feature list', e);
  }
};

export const loadRoles = async () => {
  try {
    const res = await fetchWithAuth('/roles');
    if (!res.ok) return;
    const data = await res.json();
    roles.length = 0;
    roles.push(...(Array.isArray(data) ? data : []));
    notify();
  } catch (e) {
    console.error('Failed to load roles', e);
  }
};

/** The shape the server wants: the checklist as it stands, whole. */
const bodyFor = (draft) => JSON.stringify({
  name: draft.name,
  description: draft.description || '',
  permissions: Object.entries(draft.permissions || {}).map(([feature, a]) => ({
    feature,
    view: !!a.view,
    create: !!a.create,
    edit: !!a.edit,
    delete: !!a.delete,
  })),
});

/**
 * Saves a role and returns `{ ok, error }` rather than throwing. Every caller
 * is a form that has to put the reason on screen, and a rejected save — a
 * duplicate name, a permission the caller does not hold themselves — is an
 * ordinary answer from the server, not an exception.
 */
export const saveRole = async (draft) => {
  const isNew = !draft.id;
  const res = await fetchWithAuth(isNew ? '/roles' : `/roles/${draft.id}`, {
    method: isNew ? 'POST' : 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: bodyFor(draft),
  });
  if (!res.ok) {
    let error = 'Could not save the role';
    try { error = (await res.json()).error || error; } catch { /* keep the default */ }
    return { ok: false, error };
  }
  await loadRoles();
  // The saver may have just changed their own access.
  await loadMyAccess();
  return { ok: true };
};

export const deleteRole = async (id) => {
  const res = await fetchWithAuth(`/roles/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    let error = 'Could not delete the role';
    try { error = (await res.json()).error || error; } catch { /* keep the default */ }
    return { ok: false, error };
  }
  await loadRoles();
  return { ok: true };
};

/** Groups the checklist for display, keeping the catalog's order. */
export const groupedFeatures = () => {
  const out = [];
  accessFeatures.forEach((f) => {
    const last = out[out.length - 1];
    if (last && last.group === f.group) last.items.push(f);
    else out.push({ group: f.group, items: [f] });
  });
  return out;
};

// ── Which roles a shop is actually offered ─────────────────────────────────

/** Which module a built-in role belongs to. */
const ROLE_MODULE = {
  'branch-admin': 'branches',
  'warehouse-admin': 'warehouse',
  'warehouse-user': 'warehouse',
};

/**
 * Built-in roles that are not offered unless a shop is already using them.
 *
 * Counter Staff is seeded into every organisation whether or not anybody wants
 * it, and a shop that means to define its own everyday role finds one already
 * sitting there, named and permissioned by us. A shop builds the roles it
 * needs; the only one it cannot do without is its own administrator, because
 * somebody has to be able to create the rest.
 */
const UNOFFERED = new Set(['org-user']);

/**
 * The roles an organisation may actually use, out of everything seeded for it.
 *
 * Every shop is seeded with every built-in role whatever it pays for, so one
 * with no warehouse was offered Warehouse Admin and Warehouse User, and one
 * with no branches was offered Branch Admin — roles whose screens it does not
 * have, presented as though they were a choice.
 *
 * Decided here rather than in the seeder on purpose. A module is switched on and
 * off long after an organisation is created, and this follows it the moment it
 * changes: no re-seed, and nothing deleted from a shop that turns a module off
 * for a month.
 *
 * It lives in this module because two screens ask the question and have to agree
 * on the answer. Roles & Access asks in order to list them; the user list asks
 * in order to fill the Role dropdown on an invite — and it used to not ask at
 * all, so it offered every seeded row. A shop was shown one role on the screen
 * that manages roles and five on the screen that assigns them, including a
 * Counter Staff it had never created.
 *
 * `heldIds` keeps a role that somebody is already on, whatever the modules say.
 * Dropping it would leave that person with permissions no screen can show or
 * change, and would blank the dropdown when their own record is opened.
 */
export const offeredRoles = (org, heldIds = new Set()) =>
  roles.filter((r) => {
    const held = heldIds.has(r.id);
    if (UNOFFERED.has(r.systemKey)) return held;

    const needs = ROLE_MODULE[r.systemKey];
    if (!needs) return true;
    return isModuleOn(org, needs) || held;
  });
